/* SpineMat product page (sm-pdp-* sections). Vanilla JS, no dependencies.
   Each [data-smp-main] section owns its state; the specs chart and tabs talk to it through events. */
(() => {
  'use strict';
  if (window.SMPDPLoaded) return;
  window.SMPDPLoaded = true;

  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (root, sel) => root.querySelector(sel);
  const $$ = (root, sel) => [...root.querySelectorAll(sel)];
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch { /* storage unavailable */ } },
  };

  /* ── Formatting ─────────────────────────────────────────────────────── */
  const money = (cents, cur) => {
    const r = cents / 100;
    return cur + (Number.isInteger(r) ? r.toLocaleString('en-US') : r.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };
  const thickLabel = (v) => String(v || '').replace(/\s*inch\s+ET\b/i, ' in Euro-top').replace(/\s*inch\b/i, ' in');
  const thickParts = (v) => {
    const m = String(v || '').match(/^([\d.]+)\s*inch\s*(ET)?/i);
    return m ? { size: `${m[1]} in`, tag: m[2] ? 'Euro-top' : '' } : { size: String(v || ''), tag: '' };
  };
  const parseSize = (v) => {
    const m = String(v || '').match(/([\d.]+)\s*[x×]\s*([\d.]+)/i);
    return m ? { l: Number(m[1]), w: Number(m[2]) } : null;
  };
  const dims = (l, w) => `${l}″ × ${w}″`;
  const ft = (inches) => {
    const f = Math.round((inches / 12) * 100) / 100;
    return `${f} ft`;
  };
  const cm = (inches) => Math.round(inches * 2.54);

  /* ── Header height → CSS variable (header and tabs are sticky) ───────── */
  function measureHeader() {
    const header = document.querySelector('[data-sm-section="sm-header"]')?.closest('.shopify-section, .sm-shopify-section');
    const tabs = document.querySelector('.smp-subnav');
    const root = document.documentElement.style;
    if (header) root.setProperty('--smp-header-h', `${Math.round(header.getBoundingClientRect().height)}px`);
    root.setProperty('--smp-subnav-h', tabs ? `${Math.round(tabs.getBoundingClientRect().height)}px` : '0px');
  }

  /* ══════════════════════════════════════════════════════════════════════
     Buy box
     ══════════════════════════════════════════════════════════════════════ */
  class BuyBox {
    constructor(root) {
      this.root = root;
      this.d = JSON.parse($(root, '[data-smp-data]').textContent);
      this.cur = this.d.currency || '₹';
      this.ti = this.d.thicknessIndex;
      this.si = this.d.sizeIndex;
      this.variants = this.d.variants.map((v) => ({ ...v, size: this.si >= 0 ? parseSize(v.options[this.si]) : null }));
      this.byKey = new Map(this.variants.map((v) => [v.options.join('|'), v]));
      this.sizeNames = this.parseNames(this.d.sizeNames);
      this.sized = this.si >= 0 && this.variants.every((v) => v.size);

      const fromUrl = Number(new URL(location.href).searchParams.get('variant'));
      const start = this.variants.find((v) => v.id === fromUrl) || this.variants.find((v) => v.id === this.d.selectedId) || this.variants[0];
      this.state = { variant: start, mode: 'standard', custom: { l: '', w: '' } };

      this.form = $(root, '[data-smp-form]');
      this.input = $(root, '[data-smp-variant-input]');
      if (this.input) this.input.disabled = false;
      this.qty = $(root, '.smp-qty__input');
      this.sheet = $(root, '[data-smp-sheet]');
      this.sticky = $(root, '[data-smp-sticky]');
      this.added = $(root, '[data-smp-added]');

      this.gallery = new Gallery($(root, '[data-smp-gallery]'));
      this.bind();
      this.render();
      this.initSticky();
      this.restoreRequest();
    }

    parseNames(str) {
      const map = new Map();
      String(str || '').split(',').forEach((pair) => {
        const [w, name] = pair.split(':').map((s) => s && s.trim());
        if (w && name) map.set(Number(w), name);
      });
      return map;
    }
    nameFor(w) { return this.d.showSizeNames ? this.sizeNames.get(Number(w)) || '' : ''; }

    /* Variant helpers */
    thicknessValues() { return this.ti >= 0 ? [...new Set(this.variants.map((v) => v.options[this.ti]))] : []; }
    inThickness(t) { return this.ti >= 0 ? this.variants.filter((v) => v.options[this.ti] === t) : this.variants; }
    find(t, l, w) { return this.inThickness(t).find((v) => v.size && v.size.l === l && v.size.w === w); }
    lengths(t) { return [...new Set(this.inThickness(t).map((v) => v.size.l))].sort((a, b) => a - b); }
    widths() { return [...new Set(this.variants.map((v) => v.size.w))].sort((a, b) => a - b); }
    minPrice(t) { return Math.min(...this.inThickness(t).map((v) => v.price)); }

    selectVariant(v, { silent = false } = {}) {
      if (!v) return;
      this.state.variant = v;
      this.state.mode = 'standard';
      this.render();
      if (!silent) this.root.dispatchEvent(new CustomEvent('smp:change', { bubbles: true, detail: { variant: v } }));
    }

    /* Summary pieces for the current selection */
    summaryParts() {
      const v = this.state.variant;
      const t = this.ti >= 0 ? thickLabel(v.options[this.ti]) : '';
      if (this.state.mode === 'custom') {
        const { l, w } = this.state.custom;
        return { name: 'Custom', thick: t, dims: dims(l, w), text: `Custom ${dims(l, w)}${t ? ` · ${t}` : ''}` };
      }
      if (this.sized) {
        const name = this.nameFor(v.size.w);
        return { name, thick: t, dims: dims(v.size.l, v.size.w), text: `${name ? `${name} · ` : ''}${dims(v.size.l, v.size.w)}${t ? ` · ${t}` : ''}` };
      }
      return { name: '', thick: '', dims: '', text: v.options.join(' / ') };
    }

    /* ── Render buy box from state ── */
    render() {
      const r = this.root;
      const v = this.state.variant;
      const custom = this.state.mode === 'custom';
      const s = this.summaryParts();

      const summary = $(r, '[data-smp-summary]');
      if (summary) {
        summary.innerHTML = this.sized || custom
          ? `${s.name ? `<span class="smp-select__name">${esc(s.name)}</span><span class="smp-select__sep" aria-hidden="true"></span>` : ''}`
            + `<span class="smp-select__dims">${esc(s.dims)}</span>`
            + `${s.thick ? `<span class="smp-select__sep smp-select__sep--t" aria-hidden="true"></span><span class="smp-select__thick">${esc(s.thick)}</span>` : ''}`
          : `<span>${esc(s.text)}</span>`;
      }

      // Price
      $(r, '[data-smp-standard-price]').hidden = custom;
      $(r, '[data-smp-custom-price]').hidden = !custom;
      if (!custom) {
        $(r, '[data-smp-price]').textContent = money(v.price, this.cur);
        const mrp = v.compare > v.price;
        $(r, '[data-smp-mrp]').hidden = !mrp;
        $(r, '[data-smp-off]').hidden = !mrp;
        if (mrp) {
          $(r, '[data-smp-mrp-value]').textContent = money(v.compare, this.cur);
          $(r, '[data-smp-off]').textContent = `${Math.floor(((v.compare - v.price) * 100) / v.compare)}% OFF`;
        }
        $(r, '[data-smp-price-for]').textContent = this.sized ? `For ${s.dims}${s.thick ? ` · ${s.thick}` : ''}` : s.text;
      } else {
        const near = this.nearestStandard();
        $(r, '[data-smp-custom-cap]').innerHTML = near
          ? `Never more than <strong>${esc(money(near.price, this.cur))}</strong>, the price of the next standard size (${esc(dims(near.size.l, near.size.w))}).`
          : 'Larger than our standard sizes, so our team will quote it for you.';
        $(r, '[data-smp-price-for]').textContent = `For ${s.dims}${s.thick ? ` · ${s.thick}` : ''}`;
      }

      // Variant + buttons
      if (this.input) this.input.value = v.id;
      const add = $(r, '[data-smp-add]');
      const buy = $(r, '[data-smp-buy]');
      const status = $(r, '[data-smp-status]');
      $(r, '[data-smp-order]').hidden = custom;
      const request = $(r, '[data-smp-request]');
      if (request) request.hidden = !custom;
      if (!custom) {
        [add, buy].forEach((b) => { b.disabled = !v.available; b.removeAttribute('aria-busy'); });
        $(r, '[data-smp-add-label]').textContent = v.available ? this.d.labels.add : this.d.labels.soldOut;
        $(r, '[data-smp-buy-label]').textContent = v.available ? this.d.labels.buy : this.d.labels.soldOut;
        status.classList.remove('is-ok');
        status.textContent = v.available ? '' : this.d.labels.soldOutHelp;
      }

      // Custom request fields + WhatsApp text
      const sizeText = custom ? `${dims(this.state.custom.l, this.state.custom.w)} (length × width, inches)` : '';
      const thickText = this.ti >= 0 ? v.options[this.ti] : '';
      const reqSize = $(r, '[data-smp-req-size]');
      const reqThick = $(r, '[data-smp-req-thickness]');
      if (reqSize) reqSize.value = sizeText;
      if (reqThick) reqThick.value = thickText;
      const base = this.d.whatsappMessage || '';
      const waText = custom
        ? `${base} I need a custom size: ${dims(this.state.custom.l, this.state.custom.w)} (length × width), ${thickText} thickness.`
        : `${base} Size: ${this.sized ? `${dims(v.size.l, v.size.w)}, ${thickText}` : v.options.join(' / ')}.`;
      $$(r, '[data-smp-wa], [data-smp-wa-request]').forEach((a) => { a.href = `https://wa.me/${this.d.whatsapp}?text=${encodeURIComponent(waText)}`; });

      // Sticky bar
      if (this.sticky) {
        $$(this.sticky, '[data-smp-sticky-price]').forEach((e) => { e.textContent = custom ? 'On request' : money(v.price, this.cur); });
        $$(this.sticky, '[data-smp-sticky-summary]').forEach((e) => { e.textContent = s.text; });
        $(this.sticky, '[data-smp-sticky-actions]').hidden = custom;
        $(this.sticky, '[data-smp-sticky-request]').hidden = !custom;
        $$(this.sticky, '[data-smp-sticky-add], [data-smp-sticky-buy]').forEach((b) => { b.disabled = !v.available; });
        const buyLabel = $(this.sticky, '[data-smp-sticky-buy-label]');
        if (buyLabel) buyLabel.textContent = v.available ? this.d.labels.buy : this.d.labels.soldOut;
      }

      // Shareable URL (?variant=) like Shopify's own product pages
      if (!custom) {
        const url = new URL(location.href);
        if (url.searchParams.get('variant') !== String(v.id)) {
          url.searchParams.set('variant', v.id);
          history.replaceState(history.state, '', url);
        }
      }
      this.renderSheet();
    }

    nearestStandard() {
      const { l, w } = this.state.custom;
      if (!l || !w || !this.sized) return null;
      const t = this.ti >= 0 ? this.state.variant.options[this.ti] : null;
      const fits = this.inThickness(t).filter((v) => v.size.l >= l && v.size.w >= w);
      return fits.sort((a, b) => a.price - b.price)[0] || null;
    }

    /* ══ Size chooser dialog ══ */
    openSheet(pane = 'standard') {
      this.sheetPane = pane === 'custom' ? 'custom' : this.state.mode;
      if (pane === 'custom') this.sheetPane = 'custom';
      this.renderSheet(true);
      const guide = $(this.sheet, '[data-smp-guide]');
      if (guide) guide.open = pane === 'guide';
      this.lastFocus = document.activeElement;
      this.sheet.showModal();
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        if (pane === 'guide') { guide?.scrollIntoView({ block: 'start', behavior: 'instant' }); guide?.querySelector('summary')?.focus(); }
        else if (this.sheetPane === 'custom') $(this.sheet, '[data-smp-custom-l]')?.focus();
        else ($(this.sheet, 'input[name="smp-w"]:checked') || $(this.sheet, 'input:checked'))?.focus();
      });
    }

    closeSheet() {
      if (!this.sheet.open) return;
      this.sheet.close();
    }

    onSheetClosed() {
      document.documentElement.style.overflow = '';
      // Custom size counts only once it has been confirmed with valid numbers.
      if (this.sheetPane === 'custom' && this.state.mode !== 'custom') this.sheetPane = 'standard';
      this.lastFocus?.focus?.();
    }

    renderSheet(force = false) {
      if (!this.sheet || (!this.sheet.open && !force)) return;
      const host = $(this.sheet, '[data-smp-sheet-options]');
      const v = this.state.variant;
      const t = this.ti >= 0 ? v.options[this.ti] : null;
      const sid = this.d.sectionId;
      let html = '';

      if (this.ti >= 0) {
        html += `<fieldset class="smp-step"><legend class="smp-sr">Thickness</legend><div class="smp-step__head" aria-hidden="true"><span class="smp-step__n">1</span><span class="smp-step__title">Thickness</span></div><div class="smp-thick">`;
        this.thicknessValues().forEach((val) => {
          const p = thickParts(val);
          html += `<label class="smp-opt"><input type="radio" name="smp-t-${sid}" value="${esc(val)}" ${val === t ? 'checked' : ''}><span class="smp-opt__face"><strong>${esc(p.size)}</strong>${p.tag ? `<em>${esc(p.tag)}</em>` : ''}<small>From ${esc(money(this.minPrice(val), this.cur))}</small></span></label>`;
        });
        html += `</div></fieldset>`;
      }

      if (this.sized && this.d.custom?.enabled) {
        const custom = this.sheetPane === 'custom';
        html += `<div class="smp-seg-wrap"><div class="smp-seg smp-seg--full" role="tablist" aria-label="Size type">
          <button type="button" class="smp-seg__btn" role="tab" aria-selected="${!custom}" data-smp-pane="standard">Standard sizes</button>
          <button type="button" class="smp-seg__btn" role="tab" aria-selected="${custom}" data-smp-pane="custom"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/><path d="m14.5 12.5 2-2M11.5 9.5l2-2M8.5 6.5l2-2M17.5 15.5l2-2"/></svg>Custom size</button>
        </div></div>`;
      }

      const n = this.ti >= 0 ? 2 : 1;
      if (this.sized) {
        if (this.sheetPane !== 'custom') {
          const l = v.size.l;
          html += `<div class="smp-std"><div>`;
          html += `<fieldset class="smp-step"><legend class="smp-sr">Length, head to foot</legend><div class="smp-step__head" aria-hidden="true"><span class="smp-step__n">${n}</span><span class="smp-step__title">Length</span><span class="smp-step__hint">head to foot</span></div><div class="smp-chips">`;
          this.lengths(t).forEach((len) => {
            html += `<label class="smp-opt"><input type="radio" name="smp-l-${sid}" value="${len}" ${len === l ? 'checked' : ''}><span class="smp-opt__face"><strong>${len}″</strong><small>${ft(len)}</small></span></label>`;
          });
          html += `</div></fieldset>`;
          html += `<fieldset class="smp-step"><legend class="smp-sr">Width, side to side</legend><div class="smp-step__head" aria-hidden="true"><span class="smp-step__n">${n + 1}</span><span class="smp-step__title">Width</span><span class="smp-step__hint">side to side</span></div><div class="smp-chips smp-chips--w">`;
          this.widths().forEach((w) => {
            const match = this.find(t, l, w);
            const name = this.nameFor(w);
            const label = match ? `${name ? `${esc(name)} · ` : ''}<b>${esc(money(match.price, this.cur))}</b>` : `Not in ${l}″`;
            html += `<label class="smp-opt${match && !match.available ? ' is-soldout' : ''}"><input type="radio" name="smp-w-${sid}" value="${w}" ${match ? '' : 'disabled'} ${match && match.id === v.id ? 'checked' : ''} aria-label="${w} inch width${name ? `, ${esc(name)}` : ''}${match ? `, ${esc(money(match.price, this.cur))}${match.available ? '' : ', sold out'}` : ', not made in this length'}"><span class="smp-opt__face"><strong>${w}″</strong><small>${match && !match.available ? 'Sold out' : label}</small></span></label>`;
          });
          html += `</div></fieldset></div>`;
          // Proportional top view of the chosen mattress
          const maxL = Math.max(...this.variants.map((x) => x.size.l));
          const maxW = Math.max(...this.variants.map((x) => x.size.w));
          const H = 150 * (v.size.l / maxL), W = 150 * (v.size.w / maxW);
          html += `<div class="smp-bed" aria-hidden="true"><svg viewBox="0 0 170 170"><rect x="1" y="1" width="168" height="168" rx="14" fill="#fff" stroke="#e3e9f2"/>
            <rect class="smp-bed__rect" x="${85 - W / 2}" y="${85 - H / 2}" width="${W}" height="${H}" rx="8" fill="#f0f7fe" stroke="#0b4b9c" stroke-width="2"/>
            <rect x="${85 - W / 2 + 6}" y="${85 - H / 2 + 6}" width="${Math.max(W - 12, 8)}" height="${Math.min(14, H / 6)}" rx="5" fill="#dceefb"/></svg>
            <div><strong>${dims(v.size.l, v.size.w)}</strong><span>${ft(v.size.l)} × ${ft(v.size.w)}</span><span>${cm(v.size.l)} × ${cm(v.size.w)} cm</span></div></div>`;
          html += `</div>`;
        } else {
          const { l, w } = this.state.custom;
          const near = this.nearestStandard();
          html += `<div class="smp-step"><div class="smp-step__head"><span class="smp-step__n">${n}</span><span class="smp-step__title">Your bed size</span><span class="smp-step__hint">inside the frame, in inches</span></div>
            <div class="smp-dims">
              <label class="smp-dims__field">Length <small>head to foot</small><span class="smp-dims__input"><input type="number" inputmode="decimal" min="${this.d.custom.min}" max="${this.d.custom.max}" step="0.5" value="${esc(l)}" data-smp-custom-l placeholder="e.g. 74"><span>in</span></span></label>
              <span class="smp-dims__x" aria-hidden="true">×</span>
              <label class="smp-dims__field">Width <small>side to side</small><span class="smp-dims__input"><input type="number" inputmode="decimal" min="${this.d.custom.min}" max="${this.d.custom.max}" step="0.5" value="${esc(w)}" data-smp-custom-w placeholder="e.g. 58"><span>in</span></span></label>
            </div>
            <p class="smp-dims__conv" data-smp-custom-conv>${l && w ? `= ${ft(l)} × ${ft(w)} · ${cm(l)} × ${cm(w)} cm` : 'Enter both measurements. Half inches are fine.'}</p>
            <p class="smp-dims__err" data-smp-custom-err role="alert"></p>
            <div class="smp-note"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg><div data-smp-custom-near>${this.nearHtml(near)}</div></div>
          </div>`;
        }
      } else {
        // Products without a length × width option: one chip group per option.
        this.d.options.forEach((name, idx) => {
          if (idx === this.ti) return;
          const values = [...new Set(this.variants.map((x) => x.options[idx]))];
          html += `<fieldset class="smp-step"><legend class="smp-sr">${esc(name)}</legend><div class="smp-step__head" aria-hidden="true"><span class="smp-step__n">${idx + 1}</span><span class="smp-step__title">${esc(name)}</span></div><div class="smp-chips smp-chips--w">`;
          values.forEach((val) => {
            const opts = [...v.options]; opts[idx] = val;
            const match = this.byKey.get(opts.join('|'));
            html += `<label class="smp-opt"><input type="radio" name="smp-o${idx}-${sid}" value="${esc(val)}" data-smp-generic="${idx}" ${match ? '' : 'disabled'} ${v.options[idx] === val ? 'checked' : ''}><span class="smp-opt__face"><strong>${esc(val)}</strong>${match ? `<small><b>${esc(money(match.price, this.cur))}</b></small>` : ''}</span></label>`;
          });
          html += `</div></fieldset>`;
        });
      }
      host.innerHTML = html;
      this.updateSheetFoot();
    }

    nearHtml(near) {
      const rule = esc(this.d.custom?.rule || '');
      if (!near) return `<p><strong>Tell us your size.</strong> We’ll compare it with our standard sizes.</p>${rule ? `<p>${rule}</p>` : ''}`;
      return `<p><strong>Next standard size up: ${esc(dims(near.size.l, near.size.w))} · ${esc(money(near.price, this.cur))}</strong></p>${rule ? `<p>${rule}</p>` : ''}`;
    }

    updateSheetFoot() {
      const price = $(this.sheet, '[data-smp-sheet-price]');
      const sum = $(this.sheet, '[data-smp-sheet-summary]');
      const btn = $(this.sheet, '[data-smp-sheet-confirm]');
      const v = this.state.variant;
      if (this.sheetPane === 'custom') {
        const { l, w } = this.state.custom;
        price.textContent = 'Price on confirmation';
        price.style.fontSize = '18px';
        sum.textContent = l && w ? `Custom ${dims(l, w)}${this.ti >= 0 ? ` · ${thickLabel(v.options[this.ti])}` : ''}` : 'Custom size';
        btn.textContent = 'Use this size';
      } else {
        price.style.fontSize = '';
        price.textContent = money(v.price, this.cur);
        const s = this.summaryParts();
        sum.textContent = this.state.mode === 'custom' ? (this.sized ? `${dims(v.size.l, v.size.w)} · ${thickLabel(v.options[this.ti])}` : v.options.join(' / ')) : s.text;
        btn.textContent = v.available ? 'Confirm size' : 'Close';
      }
    }

    onSheetChange(e) {
      const el = e.target;
      const v = this.state.variant;
      const t = this.ti >= 0 ? v.options[this.ti] : null;
      if (el.name?.startsWith('smp-t-')) {
        const nt = el.value;
        let next = this.sized ? this.find(nt, v.size.l, v.size.w) : this.byKey.get(Object.assign([...v.options], { [this.ti]: nt }).join('|'));
        if (!next) next = this.inThickness(nt).find((x) => x.available) || this.inThickness(nt)[0];
        this.state.variant = next;
        if (this.sheetPane !== 'custom') this.state.mode = 'standard';
        this.render();
        this.root.dispatchEvent(new CustomEvent('smp:change', { bubbles: true, detail: { variant: next } }));
        $(this.sheet, `input[name="${el.name}"]:checked`)?.focus();
        return;
      }
      if (el.name?.startsWith('smp-l-')) {
        const l = Number(el.value);
        const ws = this.widths().filter((w) => this.find(t, l, w));
        const w = ws.includes(v.size.w) ? v.size.w : ws.reduce((a, b) => (Math.abs(b - v.size.w) < Math.abs(a - v.size.w) ? b : a), ws[0]);
        this.selectVariant(this.find(t, l, w));
        $(this.sheet, `input[name="${el.name}"]:checked`)?.focus();
        return;
      }
      if (el.name?.startsWith('smp-w-')) {
        this.selectVariant(this.find(t, v.size.l, Number(el.value)));
        $(this.sheet, `input[name="${el.name}"]:checked`)?.focus();
        return;
      }
      if (el.dataset.smpGeneric) {
        const opts = [...v.options]; opts[Number(el.dataset.smpGeneric)] = el.value;
        this.selectVariant(this.byKey.get(opts.join('|')));
      }
    }

    onCustomInput() {
      const l = parseFloat($(this.sheet, '[data-smp-custom-l]')?.value);
      const w = parseFloat($(this.sheet, '[data-smp-custom-w]')?.value);
      const conv = $(this.sheet, '[data-smp-custom-conv]');
      this.state.custom = { l: Number.isFinite(l) ? l : '', w: Number.isFinite(w) ? w : '' };
      if (conv) conv.textContent = this.state.custom.l && this.state.custom.w ? `= ${ft(l)} × ${ft(w)} · ${cm(l)} × ${cm(w)} cm` : 'Enter both measurements. Half inches are fine.';
      const near = $(this.sheet, '[data-smp-custom-near]');
      if (near) near.innerHTML = this.nearHtml(this.validCustom(false) ? this.nearestStandard() : null);
      $(this.sheet, '[data-smp-custom-err]').textContent = '';
      $$(this.sheet, '.smp-dims__input').forEach((x) => x.classList.remove('is-error'));
      this.updateSheetFoot();
    }

    validCustom(report = true) {
      const { l, w } = this.state.custom;
      const { min, max } = this.d.custom;
      const bad = [];
      if (!(l >= min && l <= max)) bad.push(['l', `Length must be between ${min} and ${max} inches.`]);
      if (!(w >= min && w <= max)) bad.push(['w', `Width must be between ${min} and ${max} inches.`]);
      if (report) {
        $(this.sheet, '[data-smp-custom-err]').textContent = bad.map((b) => b[1]).join(' ');
        $(this.sheet, '[data-smp-custom-l]')?.closest('.smp-dims__input').classList.toggle('is-error', bad.some((b) => b[0] === 'l'));
        $(this.sheet, '[data-smp-custom-w]')?.closest('.smp-dims__input').classList.toggle('is-error', bad.some((b) => b[0] === 'w'));
        if (bad.length) $(this.sheet, `[data-smp-custom-${bad[0][0]}]`)?.focus();
      }
      return bad.length === 0;
    }

    confirmSheet() {
      if (this.sheetPane === 'custom') {
        if (!this.validCustom()) return;
        this.state.mode = 'custom';
        this.render();
        this.closeSheet();
        const req = $(this.root, '[data-smp-request]');
        if (req) setTimeout(() => this.reveal($(this.root, '[data-smp-price-block]')), 60);
        return;
      }
      this.state.mode = 'standard';
      this.render();
      this.closeSheet();
      this.reveal($(this.root, '[data-smp-price-block]'));
    }

    reveal(el) {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < 80 || rect.bottom > innerHeight - 90) el.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
      el.classList.remove('is-flash');
      void el.offsetWidth;
      el.classList.add('is-flash');
    }

    /* ══ Cart ══ */
    quantity() { return Math.max(1, Math.min(20, parseInt(this.qty?.value, 10) || 1)); }

    async addToCart(trigger) {
      const v = this.state.variant;
      if (!v.available) return;
      const buttons = [$(this.root, '[data-smp-add]'), $(this.root, '[data-smp-sticky-add]')].filter(Boolean);
      const label = $(this.root, '[data-smp-add-label]');
      const status = $(this.root, '[data-smp-status]');
      const fromBar = !!trigger?.closest('[data-smp-sticky]');
      buttons.forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
      if (label) label.textContent = 'Adding…';
      const mode = this.d.afterAdd || 'panel';
      const drawer = mode === 'drawer' ? document.querySelector('cart-drawer') : null;
      const body = { items: [{ id: v.id, quantity: this.quantity() }] };
      if (drawer) { body.sections = 'cart-drawer'; body.sections_url = location.pathname; }
      try {
        const res = await fetch(this.d.cartAddUrl || '/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart');
        status.classList.add('is-ok');
        status.textContent = '';
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: json }));
        if (window.publish && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'smp-buy-box', productVariantId: v.id, cartData: json });
        const item = json.items ? json.items[0] : json;
        if (mode === 'cart') { location.href = this.d.cartUrl || '/cart'; return; }
        if (mode === 'none') { status.textContent = 'Added to your cart.'; if (fromBar) toast(status.textContent); return; }
        if (!(drawer && this.openDrawer(drawer, json.sections))) this.showAdded(item, trigger);
      } catch (err) {
        status.classList.remove('is-ok');
        status.textContent = err.message || 'Something went wrong. Please try again or call us to order.';
        // The message line sits in the buy box, which may be off screen when the sticky bar was used.
        if (fromBar) toast(status.textContent);
      } finally {
        buttons.forEach((b) => { b.disabled = !v.available; b.removeAttribute('aria-busy'); });
        if (label) label.textContent = v.available ? this.d.labels.add : this.d.labels.soldOut;
      }
    }

    // The theme's (Dawn) cart drawer, refreshed from the Section Rendering API response.
    // Dawn's own renderContents() also expects a header cart bubble, which the SpineMat header doesn't have.
    openDrawer(drawer, sections) {
      const html = sections && sections['cart-drawer'];
      const current = document.getElementById('CartDrawer');
      const fresh = html && new DOMParser().parseFromString(html, 'text/html').getElementById('CartDrawer');
      if (!current || !fresh || typeof drawer.open !== 'function') return false;
      current.innerHTML = fresh.innerHTML;
      drawer.classList.remove('is-empty');
      drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close?.());
      drawer.open();
      return true;
    }

    showAdded(item, trigger) {
      const a = this.added;
      if (!a) return;
      const v = this.state.variant;
      const s = this.summaryParts();
      $(a, '[data-smp-added-variant]').textContent = s.text;
      $(a, '[data-smp-added-qty]').textContent = `Qty ${item?.quantity ?? this.quantity()}`;
      $(a, '[data-smp-added-price]').textContent = money(v.price * this.quantity(), this.cur);
      a.hidden = false;
      this.addedTrigger = trigger;
      $(a, '[id^="smp-added-title"]').focus({ preventScroll: true });
      clearTimeout(this.addedTimer);
    }
    hideAdded() {
      if (!this.added || this.added.hidden) return;
      this.added.hidden = true;
      this.addedTrigger?.focus?.({ preventScroll: true });
    }

    buyNow() {
      const v = this.state.variant;
      if (!v.available) return;
      const root = this.d.rootUrl || '/';
      location.href = `${root}cart/${v.id}:${this.quantity()}`;
    }

    /* ══ Sticky bar ══
       Desktop: shown once the buy buttons have scrolled out of view.
       Phones and tablets (.smp-sticky--pinned): always on screen; CSS shows it from the first paint. */
    initSticky() {
      if (!this.sticky) return;
      const order = $(this.root, '[data-smp-order]');
      const price = $(this.root, '[data-smp-price-block]');
      const pinned = this.sticky.classList.contains('smp-sticky--pinned');
      const small = matchMedia('(max-width: 989px)');
      // Class hooks for browsers without :has() (see sm-pdp.css).
      document.documentElement.classList.add('smp-has-sticky');
      document.documentElement.classList.toggle('smp-bar-pinned', pinned);
      const update = () => {
        let on = true;
        if (!(pinned && small.matches)) {
          const target = this.state.mode === 'custom' ? price : order;
          on = target.getBoundingClientRect().bottom < 0 && !this.sheet.open;
        }
        this.sticky.classList.toggle('is-on', on);
        this.sticky.toggleAttribute('inert', !on);
      };
      this.updateSticky = update;
      let queued = false;
      addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; update(); }); } }, { passive: true });
      addEventListener('resize', update, { passive: true });
      update();
      // The page keeps this much room at the bottom (sm-pdp.css) so the pinned bar never covers the footer.
      const setHeight = () => document.documentElement.style.setProperty('--smp-bar-h', `${Math.ceil(this.sticky.getBoundingClientRect().height)}px`);
      if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(this.sticky);
      else setHeight();
    }

    /* ══ Custom request after a contact-form round trip ══ */
    restoreRequest() {
      const ok = $(this.root, '[data-smp-request-ok]');
      if (!ok) return;
      const req = $(this.root, '[data-smp-request]');
      req.hidden = false;
      $(this.root, '[data-smp-order]').hidden = true;
      const cb = $(this.root, '[data-smp-callback]');
      if (cb) cb.open = true;
      setTimeout(() => req.scrollIntoView({ block: 'center' }), 200);
    }

    /* ══ PIN code ══ */
    checkPin(form) {
      const input = $(form, 'input[name="query"]');
      const pin = input.value.replace(/\D/g, '');
      const field = input.closest('.smp-pin__field');
      const card = form.parentElement.querySelector('[data-smp-pin-result]');
      let err = form.parentElement.querySelector('.smp-pin__error');
      if (!/^[1-9]\d{5}$/.test(pin)) {
        field.classList.add('is-error');
        if (!err) { err = document.createElement('p'); err.className = 'smp-pin__error'; err.setAttribute('role', 'alert'); form.after(err); }
        err.textContent = 'Enter a valid 6-digit PIN code.';
        input.focus();
        return;
      }
      field.classList.remove('is-error');
      err?.remove();
      input.value = pin;
      store.set('smp-pin', pin);
      $$(card, '[data-smp-pin-code]').forEach((e) => { e.textContent = pin; });
      const q = `${this.d.mapsQuery || 'SpineMat mattress store near'} ${pin}`;
      $(card, '[data-smp-pin-map]').href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
      const idle = $(card, '[data-smp-pin-idle]');
      if (idle) idle.hidden = true;
      $(card, '[data-smp-pin-done]').hidden = false;
      card.classList.add('is-done');
    }

    /* ══ Events ══ */
    bind() {
      const r = this.root;
      r.addEventListener('click', (e) => {
        const t = e.target.closest('button, a, summary');
        if (!t || !r.contains(t)) return;
        if (t.matches('[data-smp-open]')) { e.preventDefault(); this.openSheet(t.dataset.smpOpen); }
        else if (t.matches('[data-smp-sheet-close]')) this.closeSheet();
        else if (t.matches('[data-smp-sheet-confirm]')) this.confirmSheet();
        else if (t.matches('[data-smp-pane]')) {
          this.sheetPane = t.dataset.smpPane;
          if (this.sheetPane === 'standard' && this.state.mode === 'custom') { this.state.mode = 'standard'; this.render(); }
          this.renderSheet();
          $(this.sheet, `[data-smp-pane="${this.sheetPane}"]`)?.focus();
        }
        else if (t.matches('[data-smp-step]')) {
          const n = this.quantity() + Number(t.dataset.smpStep);
          this.qty.value = Math.max(1, Math.min(20, n));
          this.syncQty();
        }
        else if (t.matches('[data-smp-buy], [data-smp-sticky-buy]')) { e.preventDefault(); this.buyNow(); }
        else if (t.matches('[data-smp-add], [data-smp-sticky-add]')) { e.preventDefault(); this.addToCart(t); }
        else if (t.matches('[data-smp-sticky-request]')) {
          const req = $(r, '[data-smp-request]');
          req.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
          $(req, '[data-smp-wa-request]')?.focus({ preventScroll: true });
        }
        else if (t.matches('[data-smp-added-close]')) this.hideAdded();
      });
      // Enter in the quantity field must not submit the form natively.
      this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.addToCart($(r, '[data-smp-add]')); });
      this.qty?.addEventListener('change', () => { this.qty.value = this.quantity(); this.syncQty(); });
      this.syncQty();

      this.sheet?.addEventListener('change', (e) => this.onSheetChange(e));
      this.sheet?.addEventListener('input', (e) => { if (e.target.matches('[data-smp-custom-l], [data-smp-custom-w]')) this.onCustomInput(); });
      this.sheet?.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target.matches('[data-smp-custom-l], [data-smp-custom-w]')) { e.preventDefault(); this.confirmSheet(); } });
      this.sheet?.addEventListener('close', () => { this.onSheetClosed(); this.updateSticky?.(); });
      this.sheet?.addEventListener('click', (e) => { if (e.target === this.sheet) this.closeSheet(); });

      const pin = $(r, '[data-smp-pin]');
      if (pin) {
        pin.addEventListener('submit', (e) => { e.preventDefault(); this.checkPin(pin); });
        const input = $(pin, 'input[name="query"]');
        input.addEventListener('input', () => { input.value = input.value.replace(/\D/g, '').slice(0, 6); input.closest('.smp-pin__field').classList.remove('is-error'); });
        const saved = store.get('smp-pin');
        if (saved && /^\d{6}$/.test(saved)) { input.value = saved; this.checkPin(pin); }
      }

      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.hideAdded(); });
      document.addEventListener('click', (e) => { if (this.added && !this.added.hidden && !this.added.contains(e.target) && !e.target.closest('[data-smp-add], [data-smp-sticky-add]')) this.hideAdded(); });

      // Other sections (price chart) ask for a variant by id.
      document.addEventListener('smp:select', (e) => {
        const v = this.variants.find((x) => x.id === Number(e.detail?.id));
        if (!v) return;
        this.selectVariant(v);
      });
    }

    syncQty() {
      const q = this.quantity();
      const [minus, plus] = $$(this.root, '[data-smp-step]');
      if (minus) minus.disabled = q <= 1;
      if (plus) plus.disabled = q >= 20;
    }
  }

  /* ══════════════════════════════════════════════════════════════════════
     Gallery — native scroll-snap track, thumbs, arrows, lightbox
     ══════════════════════════════════════════════════════════════════════ */
  class Gallery {
    constructor(root) {
      this.root = root;
      if (!root) return;
      this.track = $(root, '[data-smp-track]');
      if (!this.track) return;
      this.slides = $$(root, '[data-smp-slide]');
      this.thumbs = $$(root, '[data-smp-thumb]');
      this.index = 0;
      this.lightbox = $(root, '[data-smp-lightbox]');
      root.addEventListener('click', (e) => {
        const t = e.target.closest('button');
        if (!t) return;
        if (t.matches('[data-smp-prev]')) this.go(this.index - 1);
        else if (t.matches('[data-smp-next]')) this.go(this.index + 1);
        else if (t.matches('[data-smp-thumb]')) this.go(Number(t.dataset.smpThumb));
        else if (t.matches('[data-smp-zoom]')) this.openLightbox();
        else if (t.matches('[data-smp-lightbox-close]')) this.lightbox.close();
        else if (t.matches('[data-smp-lightbox-step]')) { this.go(this.index + Number(t.dataset.smpLightboxStep), { instant: true }); this.fillLightbox(); }
      });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      });
      this.lightbox?.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { this.go(this.index + 1, { instant: true }); this.fillLightbox(); }
        if (e.key === 'ArrowLeft') { this.go(this.index - 1, { instant: true }); this.fillLightbox(); }
      });
      this.lightbox?.addEventListener('close', () => { document.documentElement.style.overflow = ''; $(root, '[data-smp-zoom]')?.focus(); });
      let queued = false;
      this.track.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; this.sync(Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth))); });
      }, { passive: true });
      this.sync(0);
    }
    go(i, { instant = false } = {}) {
      const n = this.slides.length;
      if (!n) return;
      i = (i + n) % n;
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: instant || reduced() ? 'instant' : 'smooth' });
      this.sync(i);
    }
    sync(i) {
      if (i === this.index && this.synced) return;
      this.synced = true;
      this.index = Math.max(0, Math.min(this.slides.length - 1, i));
      this.thumbs.forEach((t, k) => t.setAttribute('aria-current', k === this.index ? 'true' : 'false'));
      const label = $(this.root, '[data-smp-index]');
      if (label) label.textContent = this.index + 1;
      const thumb = this.thumbs[this.index];
      if (thumb) {
        const row = thumb.parentElement;
        const delta = thumb.getBoundingClientRect().left - row.getBoundingClientRect().left;
        const left = row.scrollLeft + delta - (row.clientWidth - thumb.clientWidth) / 2;
        row.scrollTo({ left: Math.max(0, left), behavior: reduced() ? 'instant' : 'smooth' });
      }
      this.slides.forEach((s, k) => { if (k !== this.index) s.querySelectorAll('video').forEach((v) => v.pause()); });
    }
    fillLightbox() {
      const frame = $(this.lightbox, '[data-smp-lightbox-frame]');
      const img = this.slides[this.index]?.querySelector('img');
      if (!img) return;
      const big = document.createElement('img');
      big.src = img.currentSrc || img.src;
      if (img.srcset) { big.srcset = img.srcset; big.sizes = '100vw'; }
      big.alt = img.alt;
      frame.replaceChildren(big);
    }
    openLightbox() {
      if (!this.lightbox) return;
      this.fillLightbox();
      this.lightbox.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
  }

  /* ══════════════════════════════════════════════════════════════════════
     Tabs, layers and the price chart
     ══════════════════════════════════════════════════════════════════════ */
  function initSubnav(nav) {
    const links = $$(nav, '[data-smp-subnav-link]');
    const pairs = links.map((a) => {
      const id = decodeURIComponent(a.getAttribute('href').slice(1));
      let target = document.getElementById(id);
      if (target && (target.hidden || target.closest('[hidden]') || !target.getClientRects().length)) target = null;
      a.closest('li').hidden = !target;
      return target ? [a, target] : null;
    }).filter(Boolean);
    if (!pairs.length) { nav.closest('.shopify-section, .sm-shopify-section')?.setAttribute('hidden', ''); return; }
    const setActive = () => {
      const offset = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--smp-header-h')) || 72) + nav.offsetHeight + 40;
      let active = null;
      for (const [a, t] of pairs) if (t.getBoundingClientRect().top - offset <= 0) active = a;
      links.forEach((a) => a.classList.toggle('is-active', a === active));
      if (!active) links[0]?.closest('ul')?.scrollTo({ left: 0, behavior: 'instant' });
      if (active) {
        const list = active.closest('ul');
        const a = active.getBoundingClientRect();
        const l = list.getBoundingClientRect();
        if (a.left < l.left + 16 || a.right > l.right - 28) list.scrollTo({ left: list.scrollLeft + (a.left - l.left) - 20, behavior: reduced() ? 'instant' : 'smooth' });
      }
    };
    let queued = false;
    addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; setActive(); }); } }, { passive: true });
    setActive();
  }

  // Layer list: one layer open at a time.
  function initInside(sec) {
    const details = $$(sec, '[data-smp-layer]');
    details.forEach((d) => d.addEventListener('toggle', () => {
      if (d.open) details.forEach((x) => { if (x !== d) x.open = false; });
    }));
  }

  function initChart(chart, box) {
    if (!box || !box.sized) { chart.hidden = true; return; }
    const tabsHost = $(chart, '[data-smp-chart-tabs]');
    const body = $(chart, '[data-smp-chart-body]');
    const thicks = box.thicknessValues();
    let current = box.ti >= 0 ? box.state.variant.options[box.ti] : null;

    const draw = () => {
      const lengths = [...new Set(box.variants.map((v) => v.size.l))].sort((a, b) => a - b);
      const widths = box.widths();
      const sel = box.state.mode === 'standard' ? box.state.variant.id : null;
      let html = '<table aria-label="Prices by length and width"><thead><tr><th scope="col">Length ↓ · Width →</th>';
      widths.forEach((w) => { const name = box.nameFor(w); html += `<th scope="col">${w}″${name ? `<small>${esc(name)}</small>` : ''}</th>`; });
      html += '</tr></thead><tbody>';
      lengths.forEach((l) => {
        html += `<tr><th scope="row">${l}″<small>${ft(l)}</small></th>`;
        widths.forEach((w) => {
          const v = box.find(current, l, w);
          html += v
            ? `<td><button type="button" class="smp-chart__cell" data-smp-pick="${v.id}" aria-pressed="${v.id === sel}" aria-label="${l} by ${w} inches, ${esc(money(v.price, box.cur))}${v.available ? '' : ', sold out'}">${esc(money(v.price, box.cur))}</button></td>`
            : '<td><span class="smp-chart__na" aria-label="Not available">—</span></td>';
        });
        html += '</tr>';
      });
      body.innerHTML = `${html}</tbody></table>`;
      tabsHost.innerHTML = thicks.map((t) => `<button type="button" class="smp-seg__btn" role="tab" aria-selected="${t === current}" data-smp-chart-t="${esc(t)}">${esc(thickLabel(t))}</button>`).join('');
      tabsHost.hidden = thicks.length < 2;
    };
    chart.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-smp-chart-t]');
      if (tab) { current = tab.dataset.smpChartT; draw(); chart.querySelector(`[data-smp-chart-t="${CSS.escape(current)}"]`)?.focus(); return; }
      const cell = e.target.closest('[data-smp-pick]');
      if (cell) {
        document.dispatchEvent(new CustomEvent('smp:select', { detail: { id: cell.dataset.smpPick } }));
        const v = box.state.variant;
        toast(`Selected ${dims(v.size.l, v.size.w)} · ${thickLabel(v.options[box.ti])} — ${money(v.price, box.cur)}`);
      }
    });
    document.addEventListener('smp:change', () => {
      if (box.ti >= 0) current = box.state.variant.options[box.ti];
      draw();
    });
    draw();
  }

  let toastTimer;
  function toast(text) {
    let t = document.querySelector('.smp-toast');
    if (!t) { t = document.createElement('div'); t.className = 'smp-toast'; t.setAttribute('role', 'status'); document.body.append(t); }
    t.textContent = text;
    t.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { t.hidden = true; }, 2600);
  }

  /* ── Boot ─────────────────────────────────────────────────────────── */
  const boxes = new WeakMap();
  function init(scope = document) {
    measureHeader();
    $$(scope, '[data-smp-main]').forEach((root) => { if (!boxes.has(root)) boxes.set(root, new BuyBox(root)); });
    const first = document.querySelector('[data-smp-main]');
    const box = first ? boxes.get(first) : null;
    $$(scope, '[data-smp-chart]').forEach((chart) => { if (!chart.dataset.ready) { chart.dataset.ready = '1'; initChart(chart, box); } });
    $$(scope, '[data-smp-inside]').forEach((sec) => { if (!sec.dataset.ready) { sec.dataset.ready = '1'; initInside(sec); } });
    $$(document, '[data-smp-subnav]').forEach((nav) => { if (!nav.dataset.ready) { nav.dataset.ready = '1'; initSubnav(nav); } });
  }
  addEventListener('resize', measureHeader, { passive: true });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
